// this file contains various functions and utilities used for different parts of module generation
import to from 'await-to-js'
import startCase from 'lodash.startcase'
import camelCase from 'lodash.camelcase'

// -------- REQUEST HELPERS -----------

// the request authentication takes whatever object we have in the authentication store module and sets it
export const addRequestAuthentication = (config, { rootGetters }, request, typeMappings) => {
  let authParams = rootGetters['authentication/serviceParams']
  if (authParams) {
    setFieldValues(config, request, null, authParams.authenticationMethod, undefined, typeMappings)
  }
}

// set filtering based on state parameters
export const addRequestFiltering = (config, context, request, params, typeMappings) => {
  setFieldValues(config, request, null, params, undefined, typeMappings)
}

// set pagination based on state parameters
export const addRequestPagination = (config, { state, getters }, request, typeMappings) => {
  if (config.service.features.pagination) {
    let setPaginationMethod = `set${config.service.paginationObjectType}`
    if (request[setPaginationMethod] !== undefined) {
      request[setPaginationMethod](getters._paginationObject)
    }
  } else {
    // special case - use pagination as fields if possible
    if (config.service.paginationObjectType === null) {
      setFieldValues(config, request, null, state.pagination, undefined, typeMappings)
    }
  }
}

// if the request is part of master-detail relationship, we add the master key
// TODO: allow override from params?
export const addRequestMasterKey = (config, { state }, request, params, typeMappings) => {
  if (config.items.masterKeyField && state.masterActiveKey !== undefined && state.masterActiveKey !== null) {
    setFieldValues(config, request, null, {
      [config.items.masterKeyField]: state.masterActiveKey
    }, undefined, typeMappings)
  }
}

// set sort based on state parameters
export const addRequestSorting = (config, { state, getters }, request, typeMappings) => {
  if (config.service.features.sorting) {
    let setSortingMethod = `set${pascalCase(config.service.sortFieldName)}`
    if (request[setSortingMethod] !== undefined) {
      request[setSortingMethod](getters._sortingObject)
    }
  } else {
    // special case - use sorting as fields if possible
    if (config.service.sortObjectType === null) {
      setFieldValues(config, request, null, state.sort, undefined, typeMappings)
    }
  }
}

// -------- UTILITIES -----------

// small helper to convert a camelCased string to PascalCase
// assumption: camelCasedString is really sent as input, we only capitalize first letter here
export const pascalCase = (camelCasedString) => {
  return camelCasedString[0].toUpperCase() + camelCasedString.slice(1)
}

// find the imported object and return constructor for a type (e.g. the pagination and sorting)
export const getConstructorForType = (config, objectType) => {
  // if we didn't get a type do not check the imports
  if (!objectType) {
    return null
  }
  let allImports = [config.service.import, ...config.service.typeImports]
  let importForType = allImports.find(imp => imp[objectType] !== undefined)
  if (importForType) {
    return importForType[objectType]
  } else {
    return null
  }
}

// get all available enums from configured import + type imports
// this is useful for having nicer way to set values
export const getEnums = config => {
  let res = {}
  let allImports = [config.service.import, ...config.service.typeImports]
  // all imports are checked for Object stuff
  // TODO: validate if we have some exceptions here - Object things which are not enums?
  allImports.forEach(imp => {
    let enumKeys = Object.keys(imp).filter(k => typeof imp[k] === 'object')
    enumKeys.forEach(k => {
      res[k] = imp[k]
    })
  })
  return res
}

export const getEnumsLookup = config => {
  let res = {}
  let enums = getEnums(config)
  Object.keys(enums).forEach(k => {
    res[k] = {}
    Object.keys(enums[k]).forEach(e => {
      res[k][enums[k][e]] = e
    })
  })
  return res
}

// call one or an array of hook functions
// in case when hook is array, next hook gets result of previous one
// result of last hook is returned
export const invokeHooks = async (actionType, hookName, name = hookName, { config, context, request, params, data = null }) => {
  let hookDef = actionType === 'standard' ? config.hooks[actionType][hookName] : config.hooks[actionType][hookName][name]
  if (typeof hookDef === 'function') {
    let [err, res] = await to(hookDef(config, context, request, params, data))
    if (err) {
      context.dispatch('error', `${startCase(name)}: Error while calling the ${hookName} hook`)
      return null
    } else {
      return res || request
    }
  }
  if (Array.isArray(hookDef)) {
    let req = request
    for (let i = 0; i < hookDef.length; i++) {
      let oneHook = hookDef[i]
      let [err, res] = await to(oneHook(config, context, req, params, data))
      // break processing upon first error
      if (err) {
        context.dispatch('error', `${startCase(name)}: Error while calling the ${hookName} hook #${i + 1}`)
        return null
      } else {
        // next hook gets result of current one (or the initial request if it doesn't make any changes)
        req = res || request
      }
    }
    return req
  }
  // no hooks defined
  return request
}

// turns rpc call from node-style callback into a Promise
export const promisifyRPC = (config, name, request, context, metadata = undefined) => {
  let metadataToSend = metadata || config.rpcMetadata(config, name, request, context)
  return new Promise((resolve, reject) => {
    config.service.client[name](request, metadataToSend, (err, data) => {
      if (err !== null) {
        reject(err)
      } else {
        resolve(data)
      }
    })
  })
}

// recursively create object of the target type (the GRPC type)
// based on sourceObject fields  of the "same" name (source is plain JS object)
// some fields can be excluded (used for sorting/pagination)
// steps:
// for each field to be added from source,
//    check if we can find the corresponding type in the imports
//      true => instantiate this object and go deep recursively
//      false => find setter method
//         has setter => set value
//         no setter => ignore field
// RETURN: null if target type is not found within initial call
export const setFieldValues = (config, targetObject = null, targetObjectType = null, sourceObject = {}, excludeFields = [], typeMappings = {}) => {
  let Constr = getConstructorForType(config, targetObjectType)
  // we return early only if we don't have neither constructor nor an existing object to fill in
  // or no source
  if ((!Constr && !targetObject) || !sourceObject || typeof sourceObject !== 'object') {
    return null
  }
  // prefer using existing targetObject given as param instead of creating a new one
  let obj = targetObject || new Constr()
  const doRecursiveSet = (targetObject, sourceObject, excludeFields = [], currentPath = '') => {
    // we have 2 cases: fields which have setters and maps
    // - maps do not have setters - see https://developers.google.com/protocol-buffers/docs/reference/javascript-generated#map
    let mapFields = Object.keys(Object.getPrototypeOf(targetObject))
      .filter(k => k.startsWith('get') && k.endsWith('Map'))
    mapFields.forEach(mapGetter => {
      let srcFld = camelCase(mapGetter.substr(3))
      if (sourceObject[srcFld]) {
        // support 2 types of map inputs: array and object style
        let map = targetObject[mapGetter]()
        if (Array.isArray(sourceObject[srcFld])) {
          sourceObject[srcFld].forEach(fv => {
            map.set(fv[0], fv[1])
          })
        } else {
          Object.keys(sourceObject[srcFld]).forEach(mapKey => {
            map.set(mapKey, sourceObject[srcFld][mapKey])
          })
        }
      }
    })
    // - fields with setters set values and go recursively
    let setterMethods = Object.keys(Object.getPrototypeOf(targetObject))
      .filter(k => k.startsWith('set'))
    let fieldsToSet = Object.keys(sourceObject)
      .filter(k => setterMethods.some(s => s.toLowerCase() === `set${k.toLowerCase()}`))
      .filter(k => !excludeFields.some(ef => {
        let fieldPath = currentPath === '' ? k : currentPath + '.' + k
        return ef === fieldPath
      }))
    fieldsToSet.forEach(f => {
      // sometimes the field name will be different than the type it's using
      // workaround: we can define type mappings within the action definition
      //    - in case we have a type mapping for f, we prefer it
      // TODO: are we able to get the expected type somewhere so we don't have to manually add typeMappings?
      let fieldPath = currentPath === '' ? f : currentPath + '.' + f
      let fieldType = typeMappings[fieldPath] !== undefined ? typeMappings[fieldPath] : pascalCase(f)
      let FieldConstructor = getConstructorForType(config, fieldType)
      // go deeper if field is object and we have a corresponding constructor
      // if it's null do not attempt to construct
      if (!Array.isArray(sourceObject[f]) && typeof sourceObject[f] === 'object' && sourceObject[f] !== null) {
        if (FieldConstructor) {
          let childObj = new FieldConstructor()
          doRecursiveSet(childObj, sourceObject[f], excludeFields, fieldPath)
          targetObject[`set${pascalCase(f)}`](childObj)
        } else {
          config.hooks.error(`setFieldValues: No constructor for type ${pascalCase(f)}. Skipping`)
        }
      } else {
        // if we get an array, it could be a plain object or already a constructed one
        // the first branch can handle only the plain objects: find which type they use based on field name and if it exists, create it -> do recursive set
        // also, we can only handle non-empty array here since we are testing for object kind of array elements (assumption is that all are equal to 1st)
        // since we cannot rely on typeof -> if the constructor is not Object, then we assue the array elements have already been instantiated as correct object types and just setXXXList
        // if we can't find the type, log an error
        if (Array.isArray(sourceObject[f]) && sourceObject[f].length > 0 && sourceObject[f][0].constructor === Object) {
          // field name without the List
          let fOne = f === 'afterKeyList' ? 'Afterkey' : f.endsWith('List') ? f.substring(0, f.length - 5) : f
          // we try to find a constructor for the type
          // TODO: add type mapping instead of removing 's' at the end
          // e.g. we get projectPermissionsList -> projectPermissions -> type mapping to projectPermission
          let ArrayElemConstructor = getConstructorForType(config, pascalCase(fOne)) || getConstructorForType(config, pascalCase(fOne.substr(0, fOne.length - 1)))
          if (ArrayElemConstructor) {
            // instantiate each element and push to new array
            let listElems = []
            sourceObject[f].forEach(el => {
              let childObj = new ArrayElemConstructor()
              // we set recursively from beginning (currentPath = '') and cannot exclude fields (the original list might not be good)
              // TODO: add fieldPath here if needed, something like  ...zzz[].xxx
              doRecursiveSet(childObj, el, [], '')
              listElems.push(childObj)
            })
            // finally, set the list
            targetObject[`set${pascalCase(f)}`](listElems)
          } else {
            config.hooks.error(`setFieldValues: ARRAY FILL - No constructor for type ${pascalCase(fOne)}. Skipping`)
          }
        } else {
          // fallback
          targetObject[`set${pascalCase(f)}`](sourceObject[f])
        }
      }
    })
  }
  doRecursiveSet(obj, sourceObject, excludeFields)
  return obj
}
